import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface Role {
  Id: number;
  Name: string;
  Active: boolean;
}

export interface Subsection {
  Id: number;
  Name: string;
  URL: string;
  SectionId: number;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface Section {
  Id: number;
  Name: string;
  Roles?: number[]; // Array of role IDs
  RoleNames?: string; // Comma-separated role names from backend
  Active: boolean;
  Subsections?: Subsection[];
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface CreateSectionRequest {
  Name: string;
  Roles: number[];
  Active: boolean;
}

export interface UpdateSectionRequest {
  Id: number;
  Name: string;
  Roles: number[];
  Active: boolean;
}

export interface CreateSubsectionRequest {
  Name: string;
  URL: string;
  SectionId: number;
}

export interface UpdateSubsectionRequest {
  Id: number;
  Name: string;
  URL: string;
  SectionId: number;
}

export interface SectionListResponse {
  data: Section[];
  total: number;
}

export interface SectionResponse {
  data: Section;
}

function ensureArray<T>(value: any): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && Array.isArray(value.data)) return value.data as T[];
  return [] as T[];
}

// API Service
export const sectionsApi = {
  // Section CRUD Operations
  getAll: async (): Promise<Section[]> => {
    const res = await api.get<any>('/sections');
    return ensureArray<Section>(res);
  },

  getById: (id: number): Promise<Section> =>
    api.get(`/sections/${id}`),

  create: (section: CreateSectionRequest): Promise<Section> =>
    api.post('/sections', section),

  update: (section: UpdateSectionRequest): Promise<Section> =>
    api.put(`/sections/${section.Id}`, section),

  delete: (id: number): Promise<void> =>
    api.delete(`/sections/${id}`),

  // Subsection Operations
  createSubsection: (subsection: CreateSubsectionRequest): Promise<Subsection> =>
    api.post('/subsection', subsection),

  updateSubsection: (subsection: UpdateSubsectionRequest): Promise<Subsection> =>
    api.put(`/subsection/${subsection.Id}`, subsection),

  deleteSubsection: (id: number): Promise<void> =>
    api.delete(`/subsection/${id}`),

  getSubsectionsBySectionId: async (sectionId: number): Promise<Subsection[]> => {
    const res = await api.get<any>('/subsection', { SectionId: sectionId });
    return ensureArray<Subsection>(res);
  },

  // Role Operations
  getRoles: async (): Promise<Role[]> => {
    const res = await api.get<any>('/role');
    return ensureArray<Role>(res);
  },

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<void> =>
    api.post('/sections/bulk/activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<void> =>
    api.post('/sections/bulk/deactivate', { ids }),

  // Statistics
  getStatistics: async (): Promise<{ total: number; active: number; inactive: number }> => {
    try {
      return await api.get('/sections/statistics');
    } catch (err) {
      const list = await sectionsApi.getAll();
      const total = list.length;
      const active = list.filter(s => s.Active).length;
      const inactive = total - active;
      return { total, active, inactive };
    }
  },

  // Validation helpers
  validation: {
    validateSection: (section: Partial<CreateSectionRequest> | Partial<UpdateSectionRequest> | Partial<Section>): string[] => {
      const errors: string[] = [];

      if (!section.Name || section.Name.trim().length === 0) {
        errors.push('Name is required');
      }

      if (section.Name && section.Name.trim().length > 100) {
        errors.push('Name must be 100 characters or less');
      }

      if (!section.Roles || section.Roles.length === 0) {
        errors.push('At least one role must be selected');
      }

      return errors;
    },

    validateSubsection: (subsection: Partial<CreateSubsectionRequest> | Partial<UpdateSubsectionRequest> | Partial<Subsection>): string[] => {
      const errors: string[] = [];

      if (!subsection.Name || subsection.Name.trim().length === 0) {
        errors.push('Subsection name is required');
      }

      if (subsection.Name && subsection.Name.trim().length > 100) {
        errors.push('Subsection name must be 100 characters or less');
      }

      if (!subsection.URL || subsection.URL.trim().length === 0) {
        errors.push('URL is required');
      }

      if (subsection.URL && subsection.URL.trim().length > 255) {
        errors.push('URL must be 255 characters or less');
      }

      if (!subsection.SectionId || subsection.SectionId <= 0) {
        errors.push('Section ID is required');
      }

      return errors;
    },

    validateBeforeSave: (section: CreateSectionRequest | UpdateSectionRequest): string[] => {
      const errors = sectionsApi.validation.validateSection(section);

      // Additional validation for save operations
      if (section.Roles && section.Roles.some(roleId => typeof roleId !== 'number' || roleId <= 0)) {
        errors.push('Please select valid roles');
      }

      return errors;
    }
  }
};

export default sectionsApi; 